import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { APP_CONFIG, IAppConfig } from '../../../app.config';
import { DataServiceBase } from '../../../core/service/base/data.service.base';
import { MessageService } from '../../../core/service/message.service';
import { ProgressBarService } from '../../../core/service/progress-bar.service';
import { AmortizationPlanDocument } from '../../model/amortization-plan';

@Injectable()
export class AmortizationPlanDocumentService extends DataServiceBase {
  constructor(client: HttpClient, messageService: MessageService, progressBar: ProgressBarService, @Inject(APP_CONFIG) config: IAppConfig) {
    super(client, messageService, progressBar, config, 'amortization-plan/documents');
  }

  add(model: AmortizationPlanDocument): Observable<AmortizationPlanDocument> {
    const formData = new FormData();
    formData.append('jsonData', JSON.stringify(model));
    formData.append('document', model.document, model.document.name);
    this.progressBar.start();
    return this.client.post<any>(this.endpoint, formData).pipe(
      map(response => this.mapModel(response)),
      tap(() => this.notification.showSuccess()),
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  delete(id: string): Observable<void> {
    this.progressBar.start();
    return this.client.delete<void>(`${this.endpoint}/${id}`).pipe(
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  download(model: AmortizationPlanDocument): Observable<File> {
    this.progressBar.start();
    return this.getFile(`${this.endpoint}/download/${model.id}`, 'document').pipe(
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  getByAmortizationPlan(amortizationPlanId: string): Observable<AmortizationPlanDocument[]> {
    this.progressBar.start();
    return this.client.get<any[]>(`${this.endpoint}/${amortizationPlanId}`).pipe(
      map(items => (Array.isArray(items) ? items.map(item => this.mapModel(item)) : [])),
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  private mapModel(response: any): AmortizationPlanDocument {
    return Object.assign(new AmortizationPlanDocument(), {
      id: response?.id ?? null,
      documentId: response?.documentId ?? null,
      caption: response?.caption ?? response?.fileName ?? null,
      size: response?.size ?? response?.fileSizeInBytes ?? 0,
      uploadedDate: response?.uploadedDate ? new Date(response.uploadedDate) : null,
      fileName: response?.fileName ?? null,
      fileSizeInBytes: response?.fileSizeInBytes ?? 0,
      sortOrder: response?.sortOrder ?? 0,
    });
  }
}
